/**
 * Edit Camera → General Info: per-camera "Motion" setting (ENG-2242).
 *
 * Inherit the site / Use motion / Always stream, saved on its own with a
 * PATCH of only `use_motion_override` (see cameraMotionOverride.ts for the
 * backend contract). The value rides the connector settings file, so a save
 * marks the site unsynced and says how to apply it.
 *
 * Shown to anyone with camera edit permission; without it the current choice
 * is shown read-only. Hidden on integrations where the setting means nothing
 * (SMTP, AutoPatrol, non-settings-file sites), while the general_info
 * payload doesn't carry the field, and on sites that don't use motion (see
 * motionSettingVisible).
 *
 * Choosing "Use motion" on a site that streams over RTSP shows an advisory
 * note recommending HTTP streaming (ENG-2828, motionStreamingAdvice.ts).
 */
import React from "react";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { markSiteUnsynced } from "../../utils/syncStore";
import { universalAPIErrorHandler } from "../../utils/universalAPIErrorHandler";
import { createApi } from "../../utils/createApi";
import { useAppStore } from "../../../appStore";
import {
  MOTION_CHOICES,
  MOTION_CHOICE_DESCRIPTIONS,
  MOTION_CHOICE_LABELS,
  MOTION_SAVED_NOTE,
  MOTION_SAVE_FAILED,
  MOTION_SETTING_LABEL,
  MotionChoice,
  cameraMotionFromGeneralInfo,
  choiceToOverride,
  inheritLabel,
  inheritedMotionValue,
  motionSettingVisible,
  overrideMayMatter,
  overrideToChoice,
  patchCameraMotionOverride,
  useCameraMotionStore,
} from "./cameraMotionOverride";
import { cameraMotionStreamingAdvice } from "../Sites/SiteAboutPage/SiteAboutPageUtils/motionStreamingAdvice";

interface CameraMotionSettingProps {
  cameraGeneralInfo: Record<string, any> | null | undefined;
  siteInfo: Record<string, any> | null | undefined;
}

export const CameraMotionSetting: React.FC<CameraMotionSettingProps> = ({
  cameraGeneralInfo,
  siteInfo,
}) => {
  const navigate = useNavigate();
  const { userPermissions } = useAppStore();
  const canEdit = !!userPermissions?.edit_cameras;

  const integrationTypeName: string | undefined =
    siteInfo?.integration_type_name;
  const cameraId =
    cameraGeneralInfo?.id != null ? String(cameraGeneralInfo.id) : null;

  const fromProp = cameraMotionFromGeneralInfo(cameraGeneralInfo);
  const fromStore = useCameraMotionStore((state) =>
    cameraId ? state.byCamera[cameraId] : undefined
  );
  const setCameraMotion = useCameraMotionStore(
    (state) => state.setCameraMotion
  );
  const current = fromStore ?? fromProp;
  const currentChoice = overrideToChoice(current?.override);

  // On Milestone XProtect, "inherit" means the camera's milestone_motion,
  // which general_info doesn't carry. Only needed while an override hides it
  // (with no override, the resolved use_motion already is that value).
  const [milestoneMotion, setMilestoneMotion] =
    React.useState<unknown>(undefined);
  const needsMilestoneMotion =
    integrationTypeName === "milestone" &&
    current?.override !== null &&
    current?.override !== undefined &&
    milestoneMotion === undefined;
  React.useEffect(() => {
    if (!needsMilestoneMotion || !cameraId) return;
    let cancelled = false;
    createApi("")
      .get(`/camera/${cameraId}/extra_fields/`)
      .then(
        (response: any) => {
          if (!cancelled) {
            setMilestoneMotion(response?.data?.milestone_motion ?? null);
          }
        },
        () => {
          if (!cancelled) setMilestoneMotion(null);
        }
      );
    return () => {
      cancelled = true;
    };
  }, [needsMilestoneMotion, cameraId]);

  const inherited = inheritedMotionValue({
    override: current?.override,
    effective: current?.effective,
    integrationTypeName,
    siteUseMotion: siteInfo?.use_motion,
    milestoneMotion,
  });

  const [draft, setDraft] = React.useState<MotionChoice>(currentChoice);
  const [saving, setSaving] = React.useState(false);
  const [savedNote, setSavedNote] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setDraft(currentChoice);
  }, [currentChoice, cameraId]);

  if (
    !cameraId ||
    !current ||
    !motionSettingVisible({
      integrationTypeName,
      siteUseMotion: siteInfo?.use_motion,
      cameraUsesMotion: current.effective === true,
      overrideMatters: overrideMayMatter({
        integrationTypeName,
        override: current.override,
      }),
    })
  ) {
    return null;
  }

  const dirty = canEdit && draft !== currentChoice;

  // "Use motion" on an RTSP site: recommend HTTP streaming (ENG-2828).
  // Advisory only; siteInfo is the site About payload the page already loads.
  const streamingAdvice = cameraMotionStreamingAdvice(
    siteInfo,
    draft === "motion"
  );

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    setSavedNote(false);
    const override = choiceToOverride(draft);
    try {
      const next = await patchCameraMotionOverride(cameraId, override);
      setCameraMotion(cameraId, next);
      // use_motion rides the connector settings file: needs a Sync (and, on
      // an armed site, the restart the sync prompt offers).
      markSiteUnsynced();
      setSavedNote(true);
      setSaving(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        () => {
          void save();
        },
        (context) => {
          if (context?.willRetry) return;
          setError(MOTION_SAVE_FAILED);
          setSaving(false);
        }
      );
    }
  };

  const groupName = `camera-motion-${cameraId}`;
  const optionLabel = (choice: MotionChoice) =>
    choice === "inherit"
      ? inheritLabel(inherited)
      : MOTION_CHOICE_LABELS[choice];

  return (
    <div
      className="m-4 rounded-lg border border-[#E2E8F2] bg-white px-4 py-3 font-mulish text-actuate-blue"
      data-testid="camera-motion-setting"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span id={`${groupName}-label`} className="text-[15px] font-bold">
          {MOTION_SETTING_LABEL}
        </span>
        {typeof current.effective === "boolean" && (
          <span className="rounded-full bg-[#F5F7FA] px-2.5 py-0.5 text-[12px] font-semibold text-[#5B6B86]">
            {current.effective ? "Using motion" : "Always streaming"}
          </span>
        )}
        {dirty && (
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded bg-actuate-orange px-3 py-1 text-[13px] font-bold text-white hover:bg-[#e67700] disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      <RadioGroup
        aria-labelledby={`${groupName}-label`}
        name={groupName}
        value={draft}
        onChange={(event) => {
          // Capture eagerly (see CLAUDE.md's setState-updater gotcha).
          const nextChoice = event.target.value as MotionChoice;
          setDraft(nextChoice);
          setSavedNote(false);
          setError(null);
        }}
      >
        {MOTION_CHOICES.map((choice) => (
          <div key={choice} className="mt-1">
            <FormControlLabel
              value={choice}
              disabled={!canEdit || saving}
              control={<Radio size="small" color="warning" />}
              label={
                <span className="font-mulish text-[14px] font-semibold">
                  {optionLabel(choice)}
                </span>
              }
            />
            <p className="ml-[30px] -mt-1 text-[13px] text-[#5B6B86]">
              {MOTION_CHOICE_DESCRIPTIONS[choice]}
            </p>
          </div>
        ))}
      </RadioGroup>
      {streamingAdvice && (
        <Alert
          severity="info"
          data-testid="camera-motion-streaming-advice"
          sx={{ fontFamily: "Mulish", fontSize: "13px", mt: 1 }}
        >
          {streamingAdvice}
        </Alert>
      )}
      {savedNote && (
        <p
          role="status"
          className="mt-2 rounded border-l-4 border-actuate-orange bg-[#FFF4E5] px-3 py-2 text-[13px]"
        >
          {MOTION_SAVED_NOTE}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-[13px] text-[#D93636]">
          {error}
        </p>
      )}
    </div>
  );
};
